import { useCallback, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "app/hooks";
import { misc } from "app/themes/colors";
import {
  setCustomMessage,
  setOpen as setOpenMessage
} from "components/action-snackbar/action-snackbar-slice";
import Dialog from "components/dialog";
import PatientSearch from "components/patient-search";
import BoxContentExplorer from "features/patients/sections/patient-e-documents/box-content-explorer.component";
import type { Patient } from "services/types/patients.type";

type UploadFilesFromEDocsModalProps = {
  open: boolean;
  clinicLogo: string;
  boxAccessToken: string;
  acceptedType?: string;
  fileSizeLimit?: number;
  handleClose: () => void;
  patient?: Patient | null;
  onPatientSelected?: (patient: Patient | null) => void;
  onFilesSelected: (selectedFiles: File[]) => void;
  onChangeLoadingState?: (isLoading: boolean) => void;
};

const boxOptions = {
  canShare: false,
  canDelete: false,
  canRename: false,
  canCreateNewFolder: false
};

export default function UploadFilesFromEDocsModal({
  open,
  acceptedType,
  patient,
  clinicLogo,
  boxAccessToken,
  fileSizeLimit,
  onPatientSelected,
  onFilesSelected,
  handleClose,
  onChangeLoadingState
}: UploadFilesFromEDocsModalProps) {
  const dispatch = useAppDispatch();
  const [files, setFiles] = useState<any>([]);

  const fetchFileAsFileObject = useCallback(
    async (file: any) => {
      try {
        const response = await fetch(file.authenticated_download_url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${boxAccessToken}`
          }
        });

        if (!response.ok) throw new Error("Failed to fetch file content");

        const blob = await response.blob();

        const fileObject = new File([blob], file.name, {
          type: blob.type || "application/octet-stream",
          lastModified: new Date(file.modified_at).getTime()
        });

        return fileObject;
      } catch (error) {
        console.error("Error fetching file:", { file, error });
      }
    },
    [boxAccessToken]
  );

  const onSelection = useCallback(
    async (selectedFiles: any) => {
      const files = selectedFiles.filter(({ type }: any) => type === "file");

      const allowedFiles = files.filter((file: any) => {
        const hasValidExtension =
          acceptedType && file.extension
            ? file.extension.toLowerCase() === acceptedType.toLowerCase()
            : true;

        const withinSizeLimit =
          typeof fileSizeLimit === "number" && file.size
            ? file.size <= fileSizeLimit
            : true;

        return hasValidExtension && withinSizeLimit;
      });

      const sizeText = fileSizeLimit
        ? ` under ${Math.round(fileSizeLimit / (1024 * 1024))} MB`
        : "";

      if (allowedFiles.length !== files.length) {
        dispatch(
          setCustomMessage(
            `Only ${acceptedType || ""} files${sizeText} are allowed. Non-PDF files were not added.`
          )
        );
        dispatch(setOpenMessage(5000));
      }

      setFiles(allowedFiles);
    },
    [dispatch, acceptedType, fileSizeLimit]
  );

  const handleSubmit = useCallback(async () => {
    handleClose();
    onChangeLoadingState?.(true);
    const result = await Promise.all(files.map(fetchFileAsFileObject));
    onFilesSelected?.(result.filter(Boolean));
    onChangeLoadingState?.(false);
  }, [
    files,
    handleClose,
    onFilesSelected,
    fetchFileAsFileObject,
    onChangeLoadingState
  ]);

  const isBoxConnected = Boolean(boxAccessToken && patient?.box_folder_id);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Upload files from E-Documents"
      dialogContentProps={{ sx: { display: "flex", minHeight: 768 } }}
      minWidth={1024}
    >
      <Box sx={{ width: "100%" }}>
        <Stack>
          {!patient && (
            <>
              <InputLabel>Select Patient</InputLabel>
              <PatientSearch
                onPatientSelected={(patient) => {
                  onPatientSelected?.(patient || null);
                }}
                showRecent
                sx={{ borderRadius: "6px" }}
              />
            </>
          )}
          {patient && (
            <Stack direction="row" columnGap={2} mb={1}>
              <InputLabel>Patient</InputLabel>
              <Typography variant="body2" sx={{ mt: -0.135 }}>
                {patient?.fullName}
              </Typography>
              <IconButton
                size="small"
                sx={{ mt: -0.85, ml: -2 }}
                onClick={() => {
                  onPatientSelected?.(null);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          )}
        </Stack>
        {isBoxConnected && patient && (
          <BoxContentExplorer
            folderId={String(patient.box_folder_id)}
            token={boxAccessToken}
            logoUrl={clinicLogo}
            onSelection={onSelection}
            options={boxOptions}
          />
        )}

        {!isBoxConnected && patient && (
          <Stack direction="row" alignItems="center" spacing={1} mt={2}>
            <WarningIcon sx={{ fontSize: "22px", color: misc.warningText }} />
            <Typography
              fontWeight={700}
              fontSize="16px"
              color={misc.warningText}
              sx={{ pb: 0.3 }}
            >
              E-Documents are not available for this Patient
            </Typography>
          </Stack>
        )}

        <Box sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={handleClose} sx={{ mr: 2 }}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disabled={!patient || !isBoxConnected || !files.length}
            onClick={handleSubmit}
          >
            Select
          </LoadingButton>
        </Box>
      </Box>
    </Dialog>
  );
}
